import { create } from 'zustand';
import {
    ContentStructureNavItem,
    ShopifyMenuItem,
} from '@app/lib/content-structure';
import { createRef } from 'react';

type NavTheme = 'light' | 'dark';

type OpenUIKey =
    | 'cart'
    | 'wishlist'
    | 'search-panel'
    | 'support-menu'
    | 'main-menu-mobile'
    | 'main-mobile-menu'
    | 'product-assistance'
    | 'collection-filters'
    | 'language-switch'
    | 'profile'
    | 'password-reset';

export type ProductAssistanceView = string | null;

interface UIState {
    openUIKey: null | string;
    navTheme: NavTheme;
    activeNavItem: null | ContentStructureNavItem;
    setActiveNavItem: (item: ContentStructureNavItem) => void;
    toggleUI: (key: string | null) => void;
    transitionUI: (
        fromKey: string | null,
        toKey: string | null
    ) => Promise<void>;
    toggleNavTheme: () => void;
    setNavTheme: (theme: NavTheme) => void;
    isIntersecting: boolean;
    setIntersection: (intersecting: boolean) => void;
    productAssistanceView: ProductAssistanceView;
    setProductAssistanceView: (view: ProductAssistanceView) => void;
    isWishlistItemSaved: boolean;
    toggleWishlistItem: () => void;
    productHeroRef: React.RefObject<Element>;
    setProductHeroRef: (ref: React.RefObject<Element>) => void;
    // Transient sign-out flag. Set true the moment the rider taps Log
    // out so the logout row can show a spinner and the tray can hold
    // its authed view (instead of flashing the guest screen) until the
    // session is cleared and the tray closes / redirects. Lives here
    // (not the persisted profile store) so the logout redirect's reload
    // resets it cleanly.
    loggingOut: boolean;
    setLoggingOut: (loggingOut: boolean) => void;
    // The Shopify support menu (the items the header Support drawer
    // renders). Published by SupportNav on mount so order-aware
    // surfaces in the profile tray can offer the same choices inline
    // without a second fetch. Null until the drawer has mounted or when
    // the menu fetch failed.
    supportMenuItems: ShopifyMenuItem[] | null;
    setSupportMenuItems: (items: ShopifyMenuItem[] | null) => void;
}

const useUI = create<UIState>((set, get) => ({
    openUIKey: null,
    navTheme: 'light',
    loggingOut: false,
    setLoggingOut: (loggingOut) => set(() => ({ loggingOut })),
    supportMenuItems: null,
    setSupportMenuItems: (items) => set(() => ({ supportMenuItems: items })),
    activeNavItem: null,
    setActiveNavItem: (item) => set(() => ({ activeNavItem: item })),
    toggleUI: (key) =>
        set((state) =>
            state.openUIKey === key ? { openUIKey: null } : { openUIKey: key }
        ),
    toggleNavTheme: () =>
        set((state) => ({
            navTheme: state.navTheme === 'light' ? 'dark' : 'light',
        })),
    setNavTheme: (theme) => set(() => ({ navTheme: theme })),
    isIntersecting: false,
    setIntersection: (intersecting) =>
        set(() => ({ isIntersecting: intersecting })),
    productAssistanceView: null,
    setProductAssistanceView: (view) =>
        set(() => ({ productAssistanceView: view })),
    isWishlistItemSaved: false,
    toggleWishlistItem: () =>
        set((state) => ({ isWishlistItemSaved: !state.isWishlistItemSaved })),
    productHeroRef: createRef(),
    setProductHeroRef: (ref) => set(() => ({ productHeroRef: ref })),
    transitionUI: async (fromKey: string | null, toKey: string | null) => {
        const currentUIKey = get().openUIKey;
        if (currentUIKey === fromKey) {
            set({ openUIKey: null });
            setTimeout(() => {
                set({ openUIKey: toKey });
            }, 300);
            // If the closing of the old and opening of the new
            // doesn't feel right, adjust the 300ms value
        } else {
            set((state) => ({
                openUIKey: state.openUIKey === toKey ? null : toKey,
            }));
        }
    },
}));

export default useUI;
